/**
 * Jovii — THE ONE TILE PLAYER, built once and kept for the session.
 *
 * ── WHY A TILE NEVER GETS ITS OWN IFRAME ANY MORE ───────────────────────────
 *
 * The owner's iPhone (live build, 2026-08-19): after a handful of tile
 * trailers — "I don't know if it's two or five, but it eventually stops" —
 * tiles kept expanding but no trailer ever played again for the session. The
 * mechanism was hunted with instruments, not guesses, and two measurements
 * decided this file:
 *
 *   1. **YouTube's IFrame API leaks every destroyed player.** Driving the old
 *      per-tile build through ten focus→trailer→move-on handovers in Chromium
 *      (CDP `Runtime.queryObjects` on `YT.Player.prototype`, GC forced before
 *      every count): live Player instances grew 1 → 10, one per handover, and
 *      all ten survived full teardown. The RAW API does the same with no Jovii
 *      code involved — create, PLAYING, `destroy()` (returns cleanly), remove
 *      the iframe: the Player object is still reachable afterwards, every
 *      time. Our teardown was already correct (DOM iframe count: 1 during
 *      play, 0 after); the retention is inside YouTube's widget code, below
 *      anything we can reach. A per-tile architecture therefore CANNOT be made
 *      leak-free — it can only choose how fast it accumulates.
 *
 *   2. **WebKit is the engine family that dies on media-pipeline churn.** The
 *      Linux port (WPE 26.5, GStreamer) segfaults inside libWPEWebKit itself
 *      the moment a second YouTube embed's pipeline follows a first — bare
 *      page, no Jovii code, kernel log as witness (docs/OPEN-DEFECTS.md I9,
 *      re-confirmed on webkit-2336). iOS/AVFoundation does not crash; the
 *      matching field reports are that it stops granting media pipelines to
 *      fresh embeds after a few create/destroy cycles in one page — precisely
 *      the owner's count and symptom. Unverifiable from a Linux box, said
 *      plainly; but every lever we hold points one way: STOP CHURNING.
 *
 * So there is exactly ONE tile player. It is created on the first preload,
 * reused for every tile after that (`loadVideoById` — 124–470 ms to PLAYING
 * warm, against 800–2200 ms for a fresh iframe, and the 1.1 MB player shell
 * paid once), and torn down never. The count is bounded by construction: this
 * module owns the only iframe a tile may play in, the same way
 * `trailer.svelte.ts` owns the only claim.
 *
 * ── WHY IT IS AN OVERLAY AND NOT A CHILD OF THE CARD ────────────────────────
 *
 * An iframe that is reparented RELOADS — its browsing context is discarded on
 * removal, so `appendChild` into the next tile would be exactly the churn this
 * file exists to end. (`Node.moveBefore` preserves embed state — verified in
 * Chromium: kept playing, no `load` refire — but WebKit 26.5 does not ship it,
 * and iOS is the patient.) So the frame lives in one fixed body-level host,
 * and the host is POSITIONED over the open card's frame, tracked by rAF while
 * visible: one `getBoundingClientRect` per frame on the card, a transform
 * write on the host. Between tiles the host is parked off-glass with the
 * video stopped.
 *
 * The host sits at `--z-tile-stage` — above the rails it plays over, below
 * every bar, menu and sheet. Card chrome that must ride ON the video (the
 * mute control, the award badge on an open card) is adopted into the host by
 * `stagePortal`, because the host necessarily paints over the card's own
 * stacking context.
 *
 * ── THE WIPE ────────────────────────────────────────────────────────────────
 *
 * During the card's 240 ms growth the HOST tracks the frame (its width grows
 * per frame) while the IFRAME inside stays at the final open size, anchored
 * left. The video is therefore REVEALED by the expansion — a wipe, not a
 * resize — which is the owner's "the expansion IS the reveal" made literal:
 * moving, branding-free picture is on the glass from the expansion's first
 * frame, and the embed never relayouts mid-animation. The card's height never
 * changes while it opens (Rail.svelte's `--tile-open` arithmetic), so the
 * iframe's height is exact throughout.
 *
 * Who may call what: `Poster.svelte` only, and only while it holds the one
 * trailer claim — the stage deliberately has no opinion about WHEN a trailer
 * may start (`trailer-rules.ts` owns that); it only guarantees there is never
 * a second player to start.
 */

import { revealIn, revealVerdict, type RevealVerdict } from './trailer-rules';
import { cropFactorFor } from './trailer-crop';
import { ENDED, PLAYING, attachYt, embedUrl, type YtHandle } from './yt';

/**
 * What every tile can see of the stage, as reactive state. `verdict` is the
 * readiness gate Poster's expansion rides on; `ended` is the end screen
 * arriving (the branding coming back — the card must close over it);
 * `hasPlayer` flips when the API bridge is attached, which is what lets the
 * mute control appear only once there is a player to mute.
 */
export const tileStage = $state<{
  owner: string | null;
  ytId: string | null;
  verdict: RevealVerdict;
  ended: boolean;
  attempt: number;
  hasPlayer: boolean;
  /** PLAYING has arrived for the CURRENT load — the video is provably going
   *  to play, even while the branding burn keeps the verdict at 'hold'. The
   *  6-second expansion gate reads this; the uncover still waits for 'show'. */
  rolling: boolean;
}>({ owner: null, ytId: null, verdict: 'hold', ended: false, attempt: 0, hasPlayer: false, rolling: false });

/* ── The one frame, and the plain state around it ────────────────────────── */

let host: HTMLDivElement | null = null;
let frame: HTMLIFrameElement | null = null;
let handle: YtHandle | null = null;

/** The id list for the current owner — `[first, ...alternates]`. */
let ids: string[] = [];
let attempt = 0;
let startSec = 0;

/** The reveal clock, exactly Trailer.svelte's inputs, holdMs 0 (the compact
 *  tile IS the hold; only the branding wait gates readiness). */
let openedAt = 0;
let playingAt: number | null = null;
let errored = false;
let verdictTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Which load the async work belongs to. `attachYt` resolves late and
 * `cropFactorFor` later; a stale resolution must not write over the next
 * owner's stage.
 */
let generation = 0;

/* ── The reveal verdict, recomputed on events and on its own one timer ───── */

function settleVerdict(): void {
  if (verdictTimer !== null) {
    clearTimeout(verdictTimer);
    verdictTimer = null;
  }
  if (openedAt === 0) {
    tileStage.verdict = 'hold';
    return;
  }
  const input = { openedAt, playingAt, errored, holdMs: 0 };
  const now = Date.now();
  const v = revealVerdict(input, now);
  /*
   * READY MEANS ROLLING RIGHT NOW, not "PLAYING happened once". A hidden
   * frame's playback can be quietly suspended by the engine (caught on
   * WebKit: the burn-down clock kept counting while the pipeline sat paused,
   * and the reveal uncovered YouTube's chrome flashing over a resume — the
   * exact thing this whole choreography exists to prevent). So a 'show' is
   * confirmed against the player's own state: not rolling → nudge it with a
   * muted play() (permitted for muted media everywhere), THROW AWAY the old
   * PLAYING stamp so the branding burns down fresh from the resume the nudge
   * causes, and hold until that burn completes. A device that refuses hidden
   * playback outright simply never expands — the compact tile is the honest
   * answer, and the debug read's `playerState` says why.
   */
  if (v === 'show') {
    const rolling = handle !== null && handle.state() === PLAYING;
    if (!rolling) {
      // A fresh attempt clock as well as a fresh PLAYING stamp: measured from
      // the old openedAt the silence branch would call a mere resume
      // "still-only" and the tile would give up on a video that is about to
      // roll. The nudge IS a new attempt; it gets an attempt's full patience.
      openedAt = now;
      playingAt = null;
      // Not rolling RIGHT NOW, so the expansion gate must not trust the old
      // PLAYING either — the nudge below re-earns both.
      tileStage.rolling = false;
      handle?.play();
      tileStage.verdict = 'hold';
      verdictTimer = setTimeout(settleVerdict, 300);
      return;
    }
  }
  tileStage.verdict = v;
  const wait = revealIn(input, now);
  if (Number.isFinite(wait) && wait > 0) {
    verdictTimer = setTimeout(settleVerdict, wait + 16);
  }
}

/* ── Building the one frame ──────────────────────────────────────────────── */

function ensureHost(): HTMLDivElement {
  if (host !== null) return host;
  const el = document.createElement('div');
  el.className = 'jt-tile-stage';
  // Inline because this element belongs to no component; parked off-glass.
  // PARKED MEANS OFF-GLASS AND TRANSPARENT, NEVER ZERO-SIZED: the frame keeps
  // a real layout box while hidden, so the embed's document always has honest
  // dimensions — a video decoding into a 0x0-clipped container is exactly the
  // sort of state a WebKit media pipeline may refuse (or, on the Linux port,
  // die over), and the preload's whole promise is that PLAYING arrives while
  // nobody can see it.
  el.style.cssText = [
    'position:fixed',
    'left:0',
    'top:-10000px',
    'width:362px',
    'height:204px',
    'overflow:hidden',
    'opacity:0',
    'pointer-events:none',
    'background:#000',
    'z-index:var(--z-tile-stage, 10)',
    'transition:opacity 160ms ease-out'
  ].join(';');
  document.body.appendChild(el);
  host = el;
  return el;
}

function buildFrame(firstId: string, size: { w: number; h: number }): void {
  const parent = ensureHost();
  const f = document.createElement('iframe');
  f.src = embedUrl(firstId, { muted: true, start: startSec });
  f.title = 'Trailer';
  f.setAttribute('frameborder', '0');
  f.setAttribute('loading', 'eager');
  f.referrerPolicy = 'strict-origin-when-cross-origin';
  // Same contract as Trailer.svelte's frame: WebKit wants `fullscreen` or it
  // raises a page error from inside the embed; autoplay is gated on `allow`.
  f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  // A trailer is not a control: no clicks (a click would open youtube.com),
  // no Tab stop (a focusable iframe in a rail is a keyboard trap).
  f.tabIndex = -1;
  f.style.cssText = [
    'position:absolute',
    'left:0',
    'top:0',
    'border:0',
    'display:block',
    'pointer-events:none',
    `width:${size.w}px`,
    `height:${size.h}px`,
    'transform-origin:center center',
    'transition:transform 600ms cubic-bezier(0.2, 0.8, 0.2, 1)'
  ].join(';');
  parent.appendChild(f);
  frame = f;

  void attachYt(f, {
    onready: () => {
      if (tileStage.owner === null) {
        handle?.mute();
        handle?.stop();
      } else if (tileStage.ytId !== null) {
        // A handover before readiness may have reached a not-yet-callable API.
        handle?.mute();
        handle?.load(tileStage.ytId, startSec);
      }
    },
    onplaying: () => {
      // The events are attached ONCE and answer for whatever the player is
      // showing NOW — loadCurrent resets the clock before every load, so a
      // PLAYING always lands against the current attempt's inputs.
      if (tileStage.owner === null) {
        handle?.mute();
        handle?.stop();
        return;
      }
      if (playingAt === null) playingAt = Date.now();
      tileStage.rolling = true;
      settleVerdict();
    },
    onerror: (code) => {
      if (tileStage.owner === null) return;
      // 101/150: the uploader will not allow this embedded. A third of titles
      // carry a second id; try it. Errors also arrive for loads a later owner
      // started — those carry the later state, so no generation guard here:
      // the CURRENT attempt/ids are always the ones the error is about.
      if ((code === 101 || code === 150) && attempt + 1 < ids.length) {
        attempt += 1;
        tileStage.attempt = attempt;
        loadCurrent();
      } else {
        errored = true;
        settleVerdict();
      }
    },
    onstate: (state) => {
      if (tileStage.owner === null) return;
      if (state === ENDED) tileStage.ended = true;
    }
  })
    .then((h) => {
      // The stage outlives every owner, so the handle is kept even if the
      // owner that built it has already released. It is the SESSION's player.
      handle = h;
      tileStage.hasPlayer = true;
      if (tileStage.owner === null) {
        h.mute();
        h.stop();
        return;
      }
      // A handover that landed while the bridge was still attaching could not
      // call load() (no handle yet) — the frame is still carrying the id its
      // src was built with. Retarget it now.
      if (tileStage.owner !== null && tileStage.ytId !== null && tileStage.ytId !== firstId) {
        h.mute();
        h.load(tileStage.ytId, startSec);
      }
    })
    .catch(() => {
      // The API script was blocked. SILENCE_MS turns the verdict into
      // 'still-only' shortly; the compact tile simply never expands.
    });
}

/** Point the one player at the current attempt's id. */
function loadCurrent(): void {
  const id = ids[Math.min(attempt, ids.length - 1)];
  if (id === undefined) return;
  tileStage.ytId = id;
  tileStage.ended = false;
  tileStage.rolling = false;
  openedAt = Date.now();
  playingAt = null;
  errored = false;
  settleVerdict();

  // The bar crop, off the video's own thumbnails (trailer-crop.ts). Never
  // blocks anything; a late answer eases in through the transform transition.
  const gen = generation;
  if (frame) frame.style.transform = 'scale(1)';
  void cropFactorFor(id).then((factor) => {
    if (gen === generation && frame) frame.style.transform = `scale(${factor})`;
  });

  if (frame === null) return; // buildFrame's src carries the first id
  // Every tile starts muted like every other, whatever the last one did.
  handle?.mute();
  handle?.load(id, startSec);
}

/* ── The API Poster drives ───────────────────────────────────────────────── */

/**
 * Take the stage: the preload moment, 3 s of banked dwell. The caller has
 * ALREADY taken the trailer claim — the stage's owner mirrors it, and a later
 * `stageLoad` simply retargets the one player (last-writer-wins, exactly like
 * the claim it shadows).
 *
 * `size` is the OPEN card's box — `--tile-h` by 16:9 — so YouTube picks its
 * quality rung from the real layout size even while hidden (D3: quality
 * follows layout; a phone-height frame streams `medium`).
 */
export function stageLoad(
  owner: string,
  idList: string[],
  size: { w: number; h: number },
  at?: HTMLElement | null,
  from = 0
): void {
  if (idList.length === 0) return;
  generation += 1;
  tileStage.owner = owner;
  ids = idList;
  attempt = 0;
  tileStage.attempt = 0;
  startSec = from;
  const firstBuild = frame === null;
  if (frame !== null && frame.style.width !== `${size.w}px`) {
    frame.style.width = `${size.w}px`;
    frame.style.height = `${size.h}px`;
  }
  // Sets the reveal clock and the crop either way; when the frame does not
  // exist yet it skips the load() and the src below carries the first id.
  loadCurrent();
  if (firstBuild) buildFrame(idList[0]!, size);
  /*
   * THE HIDDEN LOAD SITS AT THE CARD, transparent — the owner's literal spec
   * ("behind/under the unexpanded tile's artwork"), and the safe posture for
   * any engine whose media heuristics care where a playing frame IS: the
   * embed occupies real on-glass geometry from its first byte, it simply
   * cannot be seen. Positioned once, untracked — it is invisible and takes
   * no pointer, so drift while the page scrolls costs nothing; `stageAttach`
   * starts honest tracking the moment it is shown.
   */
  const h = host;
  if (h !== null && trackedEl === null) {
    h.style.width = `${size.w}px`;
    h.style.height = `${size.h}px`;
    if (at && at.isConnected) {
      const r = at.getBoundingClientRect();
      h.style.top = '0';
      h.style.transform = `translate(${r.left}px, ${r.top}px)`;
    }
    h.style.opacity = '0';
  }
}

/**
 * Give the stage up. A no-op for anyone but the current owner, so a late
 * teardown cannot stop the trailer that replaced it — the claim's own rule.
 * The player stays; only the video stops.
 */
export function stageRelease(owner: string): void {
  if (tileStage.owner !== owner) return;
  generation += 1;
  tileStage.owner = null;
  tileStage.ytId = null;
  tileStage.ended = false;
  tileStage.attempt = 0;
  tileStage.verdict = 'hold';
  tileStage.rolling = false;
  ids = [];
  attempt = 0;
  openedAt = 0;
  playingAt = null;
  errored = false;
  if (verdictTimer !== null) {
    clearTimeout(verdictTimer);
    verdictTimer = null;
  }
  handle?.mute();
  handle?.stop();
  park();
}

/** The live player, for the mute control. Null until the bridge attaches. */
export function stagePlayer(): YtHandle | null {
  return tileStage.hasPlayer ? handle : null;
}

/** The player's own state right now (-1 unstarted … 5 cued), for the debug
 *  read: "is the hidden preload actually rolling on this device". */
export function stagePlayerState(): number | null {
  return handle === null ? null : handle.state();
}

/* ── Showing it: the overlay ride ────────────────────────────────────────── */

let trackedEl: HTMLElement | null = null;
let trackFrame: number | null = null;
let lastRect = { left: 0, top: 0, w: 0, h: 0 };

function park(): void {
  trackedEl = null;
  if (trackFrame !== null) {
    cancelAnimationFrame(trackFrame);
    trackFrame = null;
  }
  if (host !== null) {
    host.style.opacity = '0';
    host.style.top = '-10000px';
    host.style.transform = 'none';
    // The size stays: parked is invisible, never zero-sized (see ensureHost).
    lastRect = { left: 0, top: 0, w: 0, h: 0 };
  }
}

function track(): void {
  trackFrame = null;
  const el = trackedEl;
  const h = host;
  if (el === null || h === null || !el.isConnected) return;
  const r = el.getBoundingClientRect();
  // One rect read, writes only on change. The height is constant through the
  // expansion (the rail's arithmetic), so mid-growth this is a transform and
  // a width — the host is its own compositor layer and the iframe inside
  // never relayouts.
  if (r.left !== lastRect.left || r.top !== lastRect.top) {
    h.style.transform = `translate(${r.left}px, ${r.top}px)`;
    lastRect.left = r.left;
    lastRect.top = r.top;
  }
  if (r.width !== lastRect.w) {
    h.style.width = `${r.width}px`;
    lastRect.w = r.width;
  }
  if (r.height !== lastRect.h) {
    h.style.height = `${r.height}px`;
    lastRect.h = r.height;
    // A height change is a resize (rotation, breakpoint), never the
    // expansion. Re-fit the frame to the new open box.
    if (frame !== null) {
      frame.style.height = `${r.height}px`;
      frame.style.width = `${Math.round((r.height * 16) / 9)}px`;
    }
  }
  trackFrame = requestAnimationFrame(track);
}

/**
 * Ride the open card. Called by the owner's effect the moment the expansion
 * begins; the returned cleanup is the other half. The host is positioned
 * BEFORE it is unhidden, in this same synchronous run, so there is no frame
 * of misplaced video.
 */
export function stageAttach(owner: string, cardFrame: HTMLElement): (() => void) | undefined {
  if (tileStage.owner !== owner || host === null) return undefined;
  const h = host;
  trackedEl = cardFrame;
  h.style.top = '0';
  if (typeof getComputedStyle === 'function') {
    h.style.borderRadius = getComputedStyle(cardFrame).borderRadius;
  }
  const r = cardFrame.getBoundingClientRect();
  h.style.transform = `translate(${r.left}px, ${r.top}px)`;
  h.style.width = `${r.width}px`;
  h.style.height = `${r.height}px`;
  lastRect = { left: r.left, top: r.top, w: r.width, h: r.height };
  h.style.opacity = '1';
  if (trackFrame === null && typeof requestAnimationFrame === 'function') {
    trackFrame = requestAnimationFrame(track);
  }
  return () => {
    if (trackedEl === cardFrame) park();
  };
}

/**
 * Adopt a piece of card chrome into the host, over the video — the mute
 * control, the open card's award badge. The host paints above the card's own
 * stacking context, so anything that must ride ON the picture has to live in
 * here for its duration. Svelte actions call this and undo it on teardown;
 * the node keeps its scoped classes, so its own component styles still apply.
 */
export function stagePortal(el: HTMLElement): () => void {
  const h = ensureHost();
  h.appendChild(el);
  return () => {
    if (el.parentNode === h) el.remove();
  };
}

/** Tests and the debug read. */
export function stageSnapshot(): {
  owner: string | null;
  ytId: string | null;
  verdict: RevealVerdict;
  ended: boolean;
  attempt: number;
  hasPlayer: boolean;
} {
  return { ...tileStage };
}
